var _react_refresh_temp_1;
var _react_refresh_temp_2;
_react_refresh_temp_2 = $RefreshSig$();
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { webCrypto } from '@lucidlink/ts-crypto/web-crypto';
import { translate, useModal, useToastService } from '@lucidlink/uicore';
import { FILE_PREVIEW_TELEMETRY_EVENT_NAME, getDurationIso, usePlugFilespaceClient, } from '@lucidlink/workspacemanagement';
import { generateScopeString, getPingManager, LEGACY_MAX_MISSED_PINGS, sendChunkThroughSwInternalChannel, sendWithTimeout, streamFileChunksConcurrently, streamFileChunksSequentially, } from '../file-contexts-common';
import { FilePreviewModal } from './components';
import { CONCURRENT_DOWNLOAD_SIZE_THRESHOLD, DEFAULT_STREAM_CHUNK_SIZE, FilePreviewContentToMainBroadcastMessageTypes, MainToSwBroadcastMessageTypes, SwToMainBroadcastMessageTypes, } from './constants';
import { FilePreviewMethodsContext } from './FilePreviewContext';
import { downloadRangedChunkWithQueue, postMessageToSwOnBroadcastChannel, startSWAndWaitOnActivate, } from './services';
export const FilePreviewProvider = ({ children }) => {
    _react_refresh_temp_2();
    const { closeModal: closeFilePreviewModal, openModal: openFilePreviewModal, } = useModal();
    const { plugFilespaceClient } = usePlugFilespaceClient();
    const { addToast } = useToastService();
    const filePreviewMethodsValue = useMemo(() => {
        return {
            previewFile: async ({ browserInstance, fsEntryExtension, fsEntryKey, fsEntryName, fsEntrySize, fsId, isLoggedWithCode, onDownload, runOnFilespaceClient: runOnFilespaceClientOverride, workspaceId, }) => {
                // Read bytes / log events against the guest client when an
                // override is supplied (guest browser); otherwise the standard
                // (member) filespace client.
                const runFilePreviewAction = runOnFilespaceClientOverride ??
                    ((action) => plugFilespaceClient({
                        action,
                        filespaceId: fsId,
                        workspaceId,
                    }));
                // Start tracking the time from the moment user requests the preview
                const previewInitTimestamp = Date.now();
                // The time to see a useful preview:
                // For video/audio files - the canPlay event from the HTML Media element received
                // here through the broadcast channel
                // For images / documents - when the whole file is downloaded and shown
                // We should set it only once, when the event is first triggered
                // as the event will be triggered multiple times for video/audio files
                // whenever the user skims through the timeline
                let previewStartedTimestamp = null;
                const sendFilePreviewTelemetry = (eventName, payload) => {
                    const commonData = {
                        browserInstance,
                        fileExtension: fsEntryExtension,
                        fileSize: Number(fsEntrySize),
                    };
                    void runFilePreviewAction((fsClient) => fsClient.logFilespaceEvent({
                        name: eventName,
                        data: {
                            customData: {
                                ...commonData,
                                ...payload,
                            },
                        },
                    }));
                };
                void sendFilePreviewTelemetry(FILE_PREVIEW_TELEMETRY_EVENT_NAME.FilePreviewStarted, {});
                const { promise: activationPromise, resolve: activationResolver, } = Promise.withResolvers();
                const scope = webCrypto.randomUUID();
                const scopeString = generateScopeString(scope);
                const broadcastChannel = new BroadcastChannel(scope);
                const pingManager = getPingManager({
                    maxMissedPings: LEGACY_MAX_MISSED_PINGS,
                    pingFn: (id) => {
                        postMessageToSwOnBroadcastChannel(broadcastChannel, [
                            MainToSwBroadcastMessageTypes.PING,
                            { id },
                        ]);
                    },
                });
                let serviceWorkerRegistration;
                // TODO: https://lucidlink.atlassian.net/browse/LUCID-22765
                // Here we need a lot of cleanups after modal is closed, we need to be able to stop the
                // concurrent download (although it will probably stop by itself if we close the SW), handle errors
                // in chunk downloading / stream downloading, etc, etc. Don't forget to do this when the time comes
                broadcastChannel.onmessage = async (event) => {
                    const [type, payload] = event.data;
                    switch (type) {
                        case SwToMainBroadcastMessageTypes.ACTIVATION:
                            {
                                activationResolver();
                            }
                            break;
                        case SwToMainBroadcastMessageTypes.PONG: {
                            const { id } = payload;
                            pingManager.acknowledgePing(id);
                            break;
                        }
                        case SwToMainBroadcastMessageTypes.STREAM_REQUEST: {
                            const shouldDownloadConcurrently = fsEntrySize >
                                CONCURRENT_DOWNLOAD_SIZE_THRESHOLD;
                            const downloadChunk = (buffer, offset) => runFilePreviewAction((fsClient) => fsClient
                                .fileSystem()
                                .readFile(fsEntryKey, buffer, offset));
                            const { sendChunk, sendEnd } = sendChunkThroughSwInternalChannel(serviceWorkerRegistration);
                            const chunks = shouldDownloadConcurrently
                                ? streamFileChunksConcurrently({
                                    chunkSize: DEFAULT_STREAM_CHUNK_SIZE,
                                    downloadChunk,
                                    fileSize: fsEntrySize,
                                })
                                : streamFileChunksSequentially({
                                    chunkSize: DEFAULT_STREAM_CHUNK_SIZE,
                                    downloadChunk,
                                });
                            // Small files should be streamed with the 'simpler' downloader as the concurrent one
                            // does not handle small file sizes (<1mb)
                            try {
                                for await (const chunk of chunks) {
                                    await sendWithTimeout(() => sendChunk(chunk));
                                }
                                await sendWithTimeout(() => sendEnd());
                                // Preview should be visible to user at this point as the whole file is downloaded
                                if (!previewStartedTimestamp) {
                                    previewStartedTimestamp = Date.now();
                                }
                            }
                            catch (error) {
                            }
                            // TODO: https://lucidlink.atlassian.net/browse/LUCID-22765
                            // See what to do here, take notes from file download provider
                            // This will throw if some request fails OR if SW is closed and no longer listens
                            // to messages (modal closed)
                            // We can probably safely close the BC channel, unregister sw if not already, stop
                            // ping manager, etc, etc as one sw can handle only one full file download anyways.
                            break;
                        }
                        case SwToMainBroadcastMessageTypes.CHUNK_REQUEST: {
                            const { end, start } = payload;
                            try {
                                const { chunk } = await downloadRangedChunkWithQueue({
                                    start: start,
                                    end: end,
                                    downloadChunk: (buffer, offset) => runFilePreviewAction((fsClient) => fsClient
                                        .fileSystem()
                                        .readFile(fsEntryKey, buffer, offset)),
                                });
                                postMessageToSwOnBroadcastChannel(broadcastChannel, [
                                    MainToSwBroadcastMessageTypes.CHUNK,
                                    {
                                        start,
                                        end: end,
                                        chunk,
                                    },
                                ]);
                            }
                            catch (error) {
                            }
                            break;
                        }
                        case FilePreviewContentToMainBroadcastMessageTypes.CAN_PLAY: {
                            if (!previewStartedTimestamp) {
                                previewStartedTimestamp = Date.now();
                            }
                            break;
                        }
                        default:
                            break;
                    }
                };
                try {
                    serviceWorkerRegistration = await startSWAndWaitOnActivate({
                        scope: scopeString,
                        activationPromise: activationPromise,
                    });
                }
                catch (error) {
                    broadcastChannel.close();
                    addToast({
                        content: {
                            message: translate('Please try again later'),
                            label: translate('Failed to start file preview'),
                        },
                        'data-testid': 'failure-start-file-preview-sw',
                    });
                    return;
                }
                pingManager.start();
                const onCloseModal = () => {
                    closeFilePreviewModal();
                    void serviceWorkerRegistration.unregister();
                    pingManager.stop();
                    const timeToPreview = previewStartedTimestamp
                        ? previewStartedTimestamp - previewInitTimestamp
                        : 0;
                    void sendFilePreviewTelemetry(FILE_PREVIEW_TELEMETRY_EVENT_NAME.FilePreviewEnded, {
                        timeToBeginPreview: getDurationIso(timeToPreview),
                    });
                };
                openFilePreviewModal({
                    content: (_jsx(FilePreviewModal, { closeModal: onCloseModal, fsEntryExtension: fsEntryExtension, fsEntryKey: fsEntryKey, fsEntryName: fsEntryName, fsEntrySize: fsEntrySize, fsId: fsId, isLoggedWithCode: isLoggedWithCode, onDownload: onDownload, scope: scope, workspaceId: workspaceId })),
                });
                return;
            },
        };
    }, [
        addToast,
        closeFilePreviewModal,
        openFilePreviewModal,
        plugFilespaceClient,
    ]);
    return (_jsx(FilePreviewMethodsContext.Provider, { value: filePreviewMethodsValue, children: children }));
};
_react_refresh_temp_1 = FilePreviewProvider;
$RefreshReg$(_react_refresh_temp_1, "FilePreviewProvider");
_react_refresh_temp_2(FilePreviewProvider, "bs2LkMkKRbnc1UXsjUOC/ah9xGE=", false, () => [useModal, usePlugFilespaceClient, useToastService]);
